import Form from "./Form";
import moment from 'moment';
import HTTP from "./HTTP";
import { isEmpty, parseInt, isObject, isArray, extend, forOwn, find, reduce, isNil } from 'lodash-es';

/**
 * The email message return if validation fails on an email input type
 * @type {string}
 */
const emailMsg = 'This must be a valid e-mail address';

/**
 *
 * @type {
 *	{
 *	required: (function(): *),
 *	minLength: (function(*): string),
 *	maxLength: (function(*): string),
 *	email: (function(*): boolean),
 *	maxNumber: (function(*): int)
 *	}
 * }
 */
const validations = {

	/**
	 * Checks that the input has a value
	 * @returns {*}
	 */
	required () {
		let exists;
		let value = this.newValue;

		if (this.type === 'file') {
			exists = (value.files.length === 0) ? "At least 1 file is required" : '';
		} else {
			exists = isEmpty(value) ? "This field is required" : '';
		}

		return exists;
	},

	/**
	 * Checks minlength of a given input and throws an error if it is under the min length
	 * @param validation
	 * @returns {string}
	 */
	minLength (validation) {
		return ((this.newValue !== undefined) && this.newValue.length < validation) ? "Minimum Length of " + validation : '';
	},

	/**
	 * Checks the length of the given input and throws an error if it exceeds the max length
	 * @param validation
	 * @returns {string}
	 */
	maxLength (validation) {
		return ((this.newValue !== undefined) && this.newValue.length > validation) ? "Maximum Length of " + validation : '';
	},

	/**
	 * The regex to check against an email input type
	 * @param validation
	 * @returns {boolean}
	 */
	email (validation){
		let EMAIL_REGEX = new RegExp (['^(([^<>()[\\]\\\.,;:\\s@\"]+(\\.[^<>()\\[\\]\\\.,;:\\s@\"]+)*)',
			'|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.',
			'[0-9]{1,3}\])|(([a-zA-Z\\-0-9]+\\.)+',
			'[a-zA-Z]{2,}))$'].join(''));

		return EMAIL_REGEX.test(this.newValue) ? false : validation;
	},

	/**
	 * The maximum number a given user can enter.
	 * So if the validation is cannot exceed 98 and the user enters 99; it'll throw an error message
	 * @param validation
	 * @returns {string}
	 */
	maxNumber (validation){
		return ((this.newValue !== undefined) && parseInt(this.newValue, 10) > validation) ?
			"Exceeded maximum number. Cannot exceed: " + validation :
			'';
	},

	/**
	 * Basic data validation; Not today, not in the future or not in the past
	 * Only works on full days (not hours/minutes)
	 * @param validation
	 * @returns {string}
	 */
	date (validation) {
		let today = moment().format(validation.format),
			todayTimestamp = moment(today, validation.format).format('X'),
			dateEntered = moment(this.input.value, validation.format).format('X');

		switch(validation.type) {
			case 'notToday':
				return (dateEntered === todayTimestamp) ? "Date cannot be today" : '';
			case 'notPast':
				return (dateEntered < todayTimestamp) ? "Date cannot be in the past" : '';
			case 'notFuture':
				return (dateEntered > todayTimestamp) ? "Date cannot be in the future" : '';
			default:
				return '';
		}
	},
};

export default class Inputs {

	/**
	 * Constructs an input based on the given options
	 * @param options ({})
	 * @param obj Instance of forms/modals etc
	 */
	constructor (options, obj) {
		this.label = null;
		this.name = null;
		this.type = null;
		this.multiple = false;
		this.placeholder = null;
		this.classes = [];
		this.disabled = false;
		this.action = false;
		this.text = '';
		this.areaSize = false;
		this.ignoreValidation = false;
		this.ajax = false;
		this.validateForm = false;
		this.schemaKey = null;
		this.svg = false;

		extend(this, options);

		if (typeof this.value === 'function') {
			this.value = this.value();
		}

		this.generateLabel();

		if (this.type && this[this.type]) {
			this[this.type]();
		} else {
			this.generic();
		}

		this.setPlaceHolder();
		this.setName();

		if (this.classes) {
			this.addClasses(this.input, this.classes);
		}

		this.setDisabled();
		this.addText();
		this.setAction(obj);
		this.setSVG();

		this.setFormAjaxHandler(obj);

		if (obj instanceof Form) {
			this.getExistingValue(obj.schemaValues);
		}
	}

	/**
	 * Generates a textarea
	 */
	textarea () {
		this.input = document.createElement('textarea');

		if (this.areaSize && isObject(this.areaSize)) {
			this.input.rows = this.areaSize.rows;
			this.input.cols = this.areaSize.cols;
		}
	}

	/**
	 * Generates a select input showing the options available. Default shows as "Please Select..."
	 */
	select () {
		let self = this;
		let options;
		let ObjectSet = !isArray(self.value) && isObject(self.value);

		this.input = document.createElement('select');

		let selectDefault = document.createElement('option');
		selectDefault.appendChild(document.createTextNode('Please Select...'));
		selectDefault.setAttribute('disabled', true);
		selectDefault.selected = "selected";
		self.input.appendChild(selectDefault);

		forOwn(self.value, (option, key) => {
			options = document.createElement('option');
			options.setAttribute('value', (ObjectSet) ? key : option);
			options.appendChild(document.createTextNode(option));
			self.input.appendChild(options);
		});
	}

	/**
	 *
	 */
	button () {
		this.input = document.createElement('button');
	}

	/**
	 *
	 */
	checkbox () {
		//Ticket to add - needs UX input and then implementation
	}

	/**
	 *
	 */
	radio () {
		let container, radioInput, label, span, id;

		this.input = document.createElement('div');

		this.value.forEach((radio) => {
			let text = radio.name;
			id = this.name + '_' + text;

			container = document.createElement('div');
			container.classList.add('radio-container');
			radioInput = document.createElement('input');
			radioInput.setAttribute('value', radio.value);
			radioInput.setAttribute('type', this.type);
			radioInput.setAttribute('name', this.name);
			radioInput.setAttribute('id', id);

			label = document.createElement('label');
			label.setAttribute('for', id);

			span = document.createElement('span');

			container.appendChild(radioInput);
			label.appendChild(span);
			label.appendChild(document.createTextNode(text));
			container.appendChild(label);

			this.input.appendChild(container);
		});
	}

	/**
	 * Generates a file type input - single/multiple
	 */
	file () {
		this.input = document.createElement('input');
		this.input.setAttribute('type', this.type);
	}

	/**
	 * Generates a generic input, this can be altered to be a hidden input
	 * if required and allows you to assign a value to it
	 */
	generic () {
		this.input = document.createElement('input');

		if (this.type === 'hidden' && this.value) {
			this.input.setAttribute('value', this.value);
		}

		this.input.setAttribute('type', (this.type || 'text'));
	}

	/**
	 * Generates a label for a given input (will appear to the left of the input)
	 */
	generateLabel () {
		if (this.label) {
			this.formLabel = document.createElement('label');
			this.formLabel.appendChild(document.createTextNode(this.label));

			if (this.name) {
				this.formLabel.setAttribute('for', this.name);
			}
		}
	}

	/**
	 * Sets the name of the input and applies array if applicable
	 */
	setName() {
		if (this.multiple) {
			this.input.setAttribute('name', `${this.name}[]`);
		}

		if (this.type === 'file' && this.multiple) {
			this.input.setAttribute('multiple', true);
		}

		if (this.name && !this.multiple) {
			this.input.setAttribute('name', this.name);
		}
	}

	/**
	 * Adds a list of class names to a given input
	 * @param input The input target
	 * @param classes Array of class names
	 */
	addClasses (input, classes) {
		if (!isArray(classes)) {
			throw new Error ('Classes needs defining as an array of class names');
		}

		classes.forEach((val) => {
			input.classList.add(val);
		});
	}

	/**
	 * Adds text content to a given input (allows the user to change the text value.
	 * This is so actionable buttons/submit buttons can show different text to give indication of what the action does.
	 */
	addText () {
		if (this.text) {
			if (this.type === 'submit') {
				this.input.setAttribute('value', this.text);
			} else {
				this.input.appendChild(document.createTextNode(this.text));
			}
		}
	}

	/**
	 * The error container (validation messages will appear in this)
	 */
	addErrorSpan () {
		this.inputError = document.createElement('span');
		this.inputError.classList.add('error');
	}

	/**
	 * Binds the value entered into the objects new value. This keeps track of what's going on
	 * with a given input and allows the validation, visibility features etc all to work
	 * and manipulate the form with ease.
	 */
	bindValue () {
		if (this.type === 'file') {
			this.newValue = this.input.files.length;
		} else {
			this.newValue = (this.input.value);
		}
	}

	/**
	 * This is an event listener function and is trigger each time an input is altered so it can
	 * validate a given input if there is any validation to check
	 */
	validateInput () {
		let self = this;

		if (self.inputError.hasChildNodes()){
			self.inputError.removeChild(self.inputError.lastChild);
		}

		if (self.type === 'email' && self.hasOwnProperty("validation")) {
			extend(self.validation, {email: emailMsg});
		} else if (self.type === 'email' && !self.hasOwnProperty("validation")) {
			extend(self, {validation: {email: emailMsg}});
		}

		let errorMessages = reduce(this.validation, (errors, validation, key) => {
			if (validations[key] && !isEmpty(validations[key].call(self, validation))) {
				return errors.concat(validations[key].call(self, validation));
			}

			return errors;
		}, []);

		if (!isEmpty(errorMessages)) {
			self.validationMessage = errorMessages;
			self.errorListContainer = document.createElement('ul');

			errorMessages.forEach((error) => {
				self.errorList = document.createElement('li');
				self.errorList.appendChild(document.createTextNode(error));
				self.errorListContainer.appendChild(self.errorList);
			});

			self.inputError.appendChild(self.errorListContainer);
		} else {
			self.validationMessage = [];
		}
	}

	/**
	 * Each form can have existing values, by using schemaValues: [{}], this can pre-populate a form with
	 * any existing values
	 * @param formValues
	 */
	getExistingValue (formValues) {
		let existingValue = find(formValues, {name: this.name});

		if (!existingValue || isNil(existingValue.value)) {
			return;
		}

		this.input.value = existingValue.value;
		this.bindValue();
	}

	/**
	 * Adds a placeholder to the input
	 */
	setPlaceHolder () {
		if (this.placeholder) {
			this.input.placeholder = this.placeholder;
		}
	}

	/**
	 * Disables an input
	 */
	setDisabled () {
		if (this.disabled) {
			this.input.disabled = true;
		}
	}

	/**
	 * Sets a click event if there is an action bound to the input.
	 * Adds a given instance (modals, forms etc) to the click event
	 */
	setAction (obj) {
		if (this.action && typeof this.action === 'function' && !this.validateForm) {
			this.input.addEventListener('click', this.action.bind(this, obj));
		}

		if (this.action && typeof this.action === 'function' && this.validateForm) {
			this.input.addEventListener('click', this.customActionValidation.bind(this, obj));
		}
	}

	/**
	 * Footer actions can now disable/enable the form validation prior to submission
	 * when not using the ajax call and is a new custom action
	 * @param obj
	 */
	customActionValidation (obj) {
		obj.form.dispatchEvent(obj.validateInputs);
		let errorsFound = obj.checkFormErrors();

		if (errorsFound.length <= 0) {
			this.input.addEventListener('click', this.action.call(this, obj));
		}
	}

	/**
	 * Form footer actions have the option to have an ajax submission (post or get)
	 * This will automatically generate the form data, do the HTTP request
	 * and then pass back the response to the onSuccess/onFailure callback
	 *
	 * @param obj The instance used for the footer actions
	 * Examples in the readme on how to use this.
	 */
	setFormAjaxHandler (obj) {
		if (this.ajax && isObject(this.ajax)) {
			this.input.addEventListener('click', () => {
				if (obj instanceof Form) {
					this.getExistingValue(obj.schemaValues);
					obj.form.dispatchEvent(obj.validateInputs);

					let errorsFound = this.checkFormErrors();

					if (!this.ignoreValidation && (errorsFound.length > 0 || obj.whiteListErrors.length > 0)) {
						return false;
					}
				}

				let ajaxObj = {
					url: this.ajax.url,
					timeout: 5000,
					onSuccess: (responseData) => {
						this.ajax.onSuccess(responseData);
					},
					onFailure: (status, callbackData, responseText) => {
						this.ajax.onFailure(status, callbackData, responseText);
					}
				};

				if (this.ajax.type === 'post') {
					let modalForm = obj instanceof Form ? obj.form : obj.bodyContent.querySelector('form');
					let formData = new FormData(modalForm);

					if (this.ajax.data !== undefined) {
						for(let entry of this.ajax.data.entries()) {
							formData.append(entry[0], entry[1]);
						}
					}

					extend(ajaxObj, {data: formData});
				}

				let request = new HTTP();

				request[this.ajax.type](ajaxObj);

				return true;
			});
		}
	}

	/**
	 * Appends an SVG inside a button
	 */
	setSVG() {
		if (isObject(this.svg) && this.type === 'button') {
			this.buttonSvg = this.generateSVG(this.svg.id);
			this.input.appendChild(this.buttonSvg);
		}
	}

	/**
	 * Generates an SVG
	 * @param id
	 * @returns SVG
	 */
	generateSVG (id) {
		let svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
		let use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
		use.setAttributeNS('http://www.w3.org/1999/xlink', 'href', id);
		use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', id);
		svg.appendChild(use);

		return svg;
	}

}
